<section class="surface" role="region" aria-labelledby="user-create-heading">
	<h2 id="user-create-heading" lvrPadding [padHorizontal]="32" [padVertical]="0">
		{{ 'user.create_user.title' | transloco }}
	</h2>
	<form class="form-container" [formGroup]="form" lvrAutoSave="user" [entityId]="draftId" [autoLoad]="false">
		<!-- Base User Information Section -->
		<h3 id="user-base-heading">{{ 'user.create_user.base_info' | transloco }}</h3>
		<section class="form-grid" aria-labelledby="user-base-heading" formGroupName="baseInfo">
			<!-- Title Field -->
			<lvr-field [label]="'base.field.title' | transloco">
				@let titleCtrl = form.get('baseInfo.title');
				<mat-form-field lvrInput [lvrError]="hasError(titleCtrl, 'pattern')">
					<input matInput formControlName="title" placeholder="Dr." [errorStateMatcher]="matcher" />
					@if (hasError(titleCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.title_invalid' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Placeholder (Spacer) -->
			<div></div>
			<!-- Name Field -->
			<lvr-field [label]="'base.field.last_name' | transloco | appendAsterisk">
				@let nameCtrl = form.get('baseInfo.name');
				<mat-form-field lvrInput [lvrError]="hasError(nameCtrl, 'pattern', 'required')">
					<input matInput formControlName="name" placeholder="Mustermann" [errorStateMatcher]="matcher" />
					@if (hasError(nameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.last_name_invalid' | transloco }}</mat-error>
					}
					@if (hasError(nameCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.last_name_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- FirstName Field -->
			<lvr-field [label]="'base.field.first_name' | transloco | appendAsterisk">
				@let firstNameCtrl = form.get('baseInfo.firstName');
				<mat-form-field lvrInput [lvrError]="hasError(firstNameCtrl, 'pattern', 'required')">
					<input matInput formControlName="firstName" placeholder="Max" [errorStateMatcher]="matcher" />
					@if (hasError(firstNameCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.first_name_invalid' | transloco }}</mat-error>
					}
					@if (hasError(firstNameCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.first_name_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Gender Field -->
			<lvr-field [label]="'base.field.gender' | transloco | appendAsterisk">
				@let genderCtrl = form.get('baseInfo.genderId');
				<mat-form-field lvrInput [lvrError]="hasError(genderCtrl, 'required')">
					<mat-select
						formControlName="genderId"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
					>
						@for (option of genders(); track option.id) {
							<mat-option
								[value]="option.id"
								matTooltipClass="info-tooltip"
								[matTooltip]="
									option.salutation === 'divers u.a.' ? ('client.create_client.gender_hint_divers' | transloco) : null
								"
							>
								{{ option.salutation }}
							</mat-option>
						}
					</mat-select>
					@if (hasError(genderCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.gender_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Business E-Mail Field -->
			<lvr-field [label]="'base.field.email' | transloco | appendAsterisk">
				@let emailCtrl = form.get('baseInfo.businessEmail');
				<mat-form-field lvrInput [lvrError]="hasError(emailCtrl, 'pattern', 'required', 'userExists')">
					<input
						matInput
						formControlName="businessEmail"
						placeholder="name@beispiel.de"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(emailCtrl, 'pattern')) {
						<mat-error>{{ 'base.form.error.email_invalid' | transloco }}</mat-error>
					}
					@if (hasError(emailCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.email_required' | transloco }}</mat-error>
					}
					@if (hasError(emailCtrl, 'userExists')) {
						<mat-error>{{ 'base.form.error.email_exists' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Address Field -->

			<lvr-address-completion
				[showAsterisk]="false"
				class="address-completion"
				formControlName="address"
			></lvr-address-completion>
		</section>

		<!-- Contact Point Field -->
		@for (item of contactPoints; track item; let index = $index) {
			<section class="form-grid">
				<h3 lvrPadding class="additional-communication-heading" [padVertical]="0" [padHorizontal]="8">
					<span>{{ 'base.field.contact_point.title' | transloco }}</span>
					<button matIconButton class="btn btn-tertiary" type="button" (click)="removeContactPoint(index)">
						<mat-icon class="material-symbols-outlined" aria-hidden="true">delete</mat-icon>
					</button>
				</h3>
				<lvr-contact-point
					[contactPointCategories]="contactPointCategories()"
					class="address-completion"
					[initValue]="contactPoints[$index]"
					(values)="updateValues($event, $index)"
				></lvr-contact-point>
			</section>
		}

		<section class="form-grid">
			<!-- Add additional button -->
			<lvr-button
				lvrPadding
				[padHorizontal]="32"
				[padVertical]="0"
				variant="primary"
				[label]="'base.field.contact_point.title' | transloco"
				[ariaLabel]="'base.field.contact_point.add_communication' | transloco"
				[type]="'button'"
				(clicked)="addContactPoint()"
				icon="add"
			></lvr-button>
		</section>

		<!-- Employment Information Section -->
		<h3 id="user-employment-heading">{{ 'user.create_user.employment_info' | transloco }}</h3>
		<section class="form-grid" aria-labelledby="user-employment-heading" formGroupName="employment">
			<!-- Role Field -->
			<lvr-field [label]="'base.field.role' | transloco | appendAsterisk">
				@let roleCtrl = form.get('employment.roleId');
				<mat-form-field lvrInput [lvrError]="hasError(roleCtrl, 'required')">
					<mat-select
						formControlName="roleId"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
					>
						@for (option of roles().entities; track option.id) {
							<mat-option [value]="option.id">
								{{ option.shortName }}
							</mat-option>
						}
						@if (roles().entities.length === 0) {
							<mat-option disabled>
								{{ 'base.form.warning.no_options' | transloco }}
							</mat-option>
						}
					</mat-select>
					@if (hasError(roleCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.role_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Spezialisierungs Feld -->
			@if (showSpecialization()) {
				<lvr-field [label]="'base.field.specialization' | transloco">
					<mat-form-field lvrInput>
						<mat-select formControlName="specializationId" [placeholder]="'base.field.select_please' | transloco">
							@for (option of specializations().entities; track option.id) {
								<mat-option [value]="option.id">
									{{ option.shortName }}
								</mat-option>
							}
							@if (specializations().entities.length === 0) {
								<mat-option disabled>
									{{ 'base.form.warning.no_options' | transloco }}
								</mat-option>
							}
						</mat-select>
					</mat-form-field>
				</lvr-field>
			}
			<!-- Service Office Field -->
			<lvr-field [label]="'user.create_user.ifd_label' | transloco | appendAsterisk">
				@let ifdCtrl = form.get('employment.integrationServiceOfficeId');
				<mat-form-field lvrInput [lvrError]="hasError(ifdCtrl, 'required')">
					<mat-select
						formControlName="integrationServiceOfficeId"
						[placeholder]="'base.field.select_please' | transloco"
						aria-required="true"
					>
						@for (option of integrationOffices().entities; track option.id) {
							<mat-option [value]="option.id">
								{{ option.shortName }}
							</mat-option>
						}
						@if (integrationOffices().entities.length === 0) {
							<mat-option disabled>
								{{ 'base.form.warning.no_options' | transloco }}
							</mat-option>
						}
					</mat-select>
					@if (hasError(ifdCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.integration_service_office_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			@let serviceCtrl = form.get('employment.serviceId');
			<lvr-field
				[label]="
					isInclusionOfficeSelected()
						? ('user.create_user.service_label' | transloco)
						: ('user.create_user.service_label' | transloco | appendAsterisk)
				"
			>
				<mat-form-field lvrInput [lvrError]="!isInclusionOfficeSelected() && hasError(serviceCtrl, 'required')">
					<mat-select
						formControlName="serviceId"
						[placeholder]="'base.field.select_please' | transloco"
						[attr.aria-required]="isInclusionOfficeSelected() ? 'false' : 'true'"
					>
						@for (option of services$ | async; track option.id) {
							<mat-option [value]="option.id">
								{{ option.shortName }}
							</mat-option>
						}
						@if ((services$ | async)?.length === 0) {
							<mat-option disabled>
								{{ 'base.form.warning.no_options' | transloco }}
							</mat-option>
						}
					</mat-select>
					@if (!isInclusionOfficeSelected() && hasError(serviceCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.service_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
		</section>

		<h3 id="user-position-share-heading">{{ 'user.create_user.position_share_info' | transloco }}</h3>
		<section class="form-grid" aria-labelledby="user-position-share-heading" formGroupName="employment">
			<!-- Employment Start Field -->
			<lvr-field [label]="'user.create_user.position_share_since' | transloco | appendAsterisk">
				@let startCtrl = form.get('employment.start');
				<mat-form-field
					lvrInput
					[lvrError]="hasError(startCtrl, 'matDatepickerParse', 'matDatepickerMin', 'matDatepickerMax', 'required')"
				>
					<input
						matInput
						[matDatepicker]="picker"
						formControlName="start"
						[placeholder]="'base.field.select_please' | transloco"
						[errorStateMatcher]="matcher"
						[max]="maxDate()"
						[min]="minDate()"
					/>
					<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
					<mat-datepicker #picker></mat-datepicker>
					@if (hasError(startCtrl, 'matDatepickerParse')) {
						<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
					} @else if (hasError(startCtrl, 'matDatepickerMin')) {
						<mat-error>{{ 'base.form.error.min_years_error' | transloco: { min: gracePeriod } }}</mat-error>
					} @else if (hasError(startCtrl, 'matDatepickerMax')) {
						<mat-error>{{ 'base.form.error.date_max_error' | transloco }}</mat-error>
					} @else if (hasError(startCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.employment_start_required' | transloco }}</mat-error>
					} @else {
						<mat-hint>{{ dateFormatHint }}</mat-hint>
					}
				</mat-form-field>
			</lvr-field>
			<!-- Position Share Field -->
			<lvr-field [label]="'base.field.positionShare' | transloco | appendAsterisk">
				@let positionShareCtrl = form.get('employment.positionShare');
				<mat-form-field lvrInput [lvrError]="hasError(positionShareCtrl, 'min', 'max', 'pattern', 'required')">
					<input
						matInput
						type="number"
						step="0.001"
						formControlName="positionShare"
						placeholder="0.25"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(positionShareCtrl, 'min', 'max', 'pattern')) {
						<mat-error>{{ 'base.form.error.position_share_error' | transloco }}</mat-error>
					} @else if (hasError(positionShareCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.positionShare_required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
		</section>

		<p class="required-hint">{{ 'base.form.required' | transloco }}</p>

		<lvr-default-actions
			[disableSave]="form.invalid || !canCreateUsersPermission() || loading()"
			(save)="submit()"
			(cancelSave)="cancel()"
			[disableCancelSave]="loading()"
			[showLoading]="loading()"
		>
			<lvr-restore-actions entityType="user" [entityId]="draftId"></lvr-restore-actions>
		</lvr-default-actions>
	</form>
</section>
